/**
 * Centralized API Configuration
 *
 * Builds API base URL from environment variables to avoid hardcoded values.
 * Works in both development and production environments with enhanced validation.
 *
 * Environment Variables:
 * - VITE_API_ENDPOINT: Full API URL for production (e.g., https://api.autar.ai)
 * - VITE_SERVER_HOST: Server hostname for development (default: localhost)
 * - VITE_SERVER_PORT: Server port for development (default: 3000)
 * - VITE_USE_HTTPS: Use HTTPS in development (default: false)
 * - VITE_API_TIMEOUT: Request timeout in milliseconds (default: 30000)
 * - VITE_HEALTH_CHECK_INTERVAL: Health check interval in milliseconds (default: 30000)
 * - VITE_ENABLE_OFFLINE_MODE: Enable offline mode fallback (default: false)
 * - VITE_SHOW_DEBUG_INFO: Show debug information in development (default: false)
 */

import { logger } from '../utils/logger';

export interface APIConfig {
  baseUrl: string;
  apiUrl: string;
  socketUrl: string;
  timeout: number;
  healthCheckInterval: number;
  healthCheckEndpoint: string;
  isDevelopment: boolean;
  enableOfflineMode: boolean;
  showDebugInfo: boolean;
}

export interface ConfigValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

const validateEnvironmentConfig = (): ConfigValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if we're in production without an API endpoint configured
  if (import.meta.env.PROD && !import.meta.env.VITE_API_ENDPOINT && !import.meta.env.VITE_API_URL) {
    warnings.push(
      'Running in production mode without VITE_API_ENDPOINT or VITE_API_URL. Using relative URLs.',
    );
  }

  // Validate port number
  const port = import.meta.env.VITE_SERVER_PORT;
  if (port && (isNaN(Number(port)) || Number(port) < 1 || Number(port) > 65535)) {
    errors.push(`Invalid VITE_SERVER_PORT: ${port}. Must be a number between 1 and 65535.`);
  }

  // Validate timeout
  const timeout = import.meta.env.VITE_API_TIMEOUT;
  if (timeout && (isNaN(Number(timeout)) || Number(timeout) < 1000)) {
    errors.push(`Invalid VITE_API_TIMEOUT: ${timeout}. Must be a number >= 1000 milliseconds.`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, '');

const resolveProtocol = (): 'http' | 'https' => {
  if (import.meta.env.VITE_USE_HTTPS === 'true') {
    return 'https';
  }

  if (typeof window !== 'undefined' && window.location.protocol) {
    return window.location.protocol.replace(':', '') as 'http' | 'https';
  }

  return 'http';
};

const resolveHost = (): string => {
  if (typeof window !== 'undefined' && window.location.hostname) {
    return window.location.hostname;
  }

  if (import.meta.env.VITE_SERVER_HOST) {
    return import.meta.env.VITE_SERVER_HOST;
  }

  return 'localhost';
};

const resolvePort = (): string | undefined => {
  const candidates: Array<string | undefined> = [import.meta.env.VITE_SERVER_PORT];

  if (typeof window !== 'undefined' && window.location.port) {
    candidates.push(window.location.port);
  }

  candidates.push('3000');
  candidates.push('3001');

  const devServerPorts = new Set<string>(['5173', '5174', '5175']);

  const seen = new Set<string>();
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }

    if (candidate === '80' || candidate === '443') {
      continue;
    }

    if (devServerPorts.has(candidate)) {
      continue;
    }

    if (seen.has(candidate)) {
      continue;
    }

    seen.add(candidate);
    return candidate;
  }

  return undefined;
};

const getApiBaseUrl = (): string => {
  if (import.meta.env.VITE_API_SAME_ORIGIN === 'true') {
    return '';
  }

  // Support both VITE_API_ENDPOINT and VITE_API_URL for backwards compatibility
  const explicitEndpoint = import.meta.env.VITE_API_ENDPOINT || import.meta.env.VITE_API_URL;

  // In production, prefer explicit API endpoint if provided.
  if (import.meta.env.PROD) {
    return explicitEndpoint ? trimTrailingSlash(explicitEndpoint) : '';
  }

  if (explicitEndpoint) {
    try {
      const parsed = new URL(explicitEndpoint);
      return trimTrailingSlash(parsed.origin);
    } catch {
      return trimTrailingSlash(explicitEndpoint);
    }
  }

  const protocol = resolveProtocol();
  const host = resolveHost();
  const port = resolvePort();

  const origin = port ? `${protocol}://${host}:${port}` : `${protocol}://${host}`;
  return trimTrailingSlash(origin);
};

const getSocketUrl = (): string => {
  if (import.meta.env.VITE_API_SAME_ORIGIN === 'true') {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }

  // In production, use VITE_API_ENDPOINT or VITE_API_URL for socket connection if provided
  // Otherwise fall back to same origin
  if (import.meta.env.PROD) {
    return (
      import.meta.env.VITE_API_ENDPOINT || import.meta.env.VITE_API_URL || window.location.origin
    );
  }

  // In development, use the same base URL as API
  return getApiBaseUrl();
};

const createAPIConfig = (): APIConfig => {
  const baseUrl = getApiBaseUrl();

  return {
    baseUrl,
    apiUrl: baseUrl,
    socketUrl: getSocketUrl(),
    timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,
    healthCheckInterval: Number(import.meta.env.VITE_HEALTH_CHECK_INTERVAL) || 30000,
    healthCheckEndpoint: '/health',
    isDevelopment: import.meta.env.DEV,
    enableOfflineMode: import.meta.env.VITE_ENABLE_OFFLINE_MODE === 'true',
    showDebugInfo: import.meta.env.VITE_SHOW_DEBUG_INFO === 'true',
  };
};

// Validate configuration on module load
const validation = validateEnvironmentConfig();
if (!validation.isValid) {
  logger.error('API Configuration Errors:', { errors: validation.errors });
  if (import.meta.env.DEV) {
    // In development, show configuration errors prominently
    const errorMessage = `API Configuration Error:\n${validation.errors.join('\n')}`;
    logger.error(errorMessage);
  }
}

if (validation.warnings.length > 0) {
  logger.warn('API Configuration Warnings:', { warnings: validation.warnings });
}

export const API_CONFIG = createAPIConfig();

export default API_CONFIG;
